import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { ThemeProvider, css } from 'styled-components';
import { DndContext, closestCenter, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates,  arrayMove } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { rgba, darken, lighten } from 'polished';
import {
  FaHome, FaExchangeAlt, FaFileInvoiceDollar, FaBox, FaCameraRetro,
  FaLayerGroup, FaShoppingCart, FaSitemap, FaUsers, FaUserShield, FaCut,
  FaChartBar, FaChevronLeft, FaChevronRight, FaMoneyCheckAlt, FaTimes,
  FaReceipt, FaCog, FaWallet, FaCalendarCheck, FaCheckDouble, FaBuilding,
  FaSitemap as FaDepartment, FaShieldAlt, FaExclamationTriangle, FaEye,
  FaEyeSlash, FaChartLine, FaBreadSlice, FaFileAlt, FaTruck, FaDollarSign, 
  FaBoxes, FaWarehouse, FaUtensils, FaBalanceScale, FaCamera, FaPhotoVideo,
  FaClipboardCheck, FaChevronUp, FaChevronDown, FaIdCard, FaCar, FaRoute, FaWrench
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

const defaultTheme = {
  primary: {
    main: '#336699',
    light: '#4073A2',
    dark: '#245180',
    contrast: '#FFFFFF',
  },
  background: {
    main: '#283747',
    light: '#34465A',
    dark: '#1C2834',
  },
  text: {
    primary: '#FFFFFF',
    secondary: rgba('#FFFFFF', 0.7),
  },
  accent: '#FFC107',
  border: rgba('#FFFFFF', 0.1),
  shadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  transition: {
    default: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    slower: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

const SIDEBAR_WIDTHS = {
  expanded: '280px',
  collapsed: '75px',
  mobile: '280px',
};

const BREAKPOINTS = {
  mobile: '768px',
  tablet: '1024px',
};

const mobileStyles = css`
  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: fixed;
    left: ${props => props.isOpen ? '0' : '-100%'};
    width: ${SIDEBAR_WIDTHS.mobile} !important;
    z-index: 1001;
    transition: left ${props => props.theme.transition.slower};
    box-shadow: ${props => props.isOpen ? props.theme.shadow : 'none'};
  }
`;

const SidebarContainer = styled.aside`
  width: ${props =>
    props.isMobile
      ? SIDEBAR_WIDTHS.mobile
      : props.collapsed
        ? SIDEBAR_WIDTHS.collapsed
        : SIDEBAR_WIDTHS.expanded};
  position: fixed;
  top: 0; // Changed from headerHeight
  height: 100vh; // Changed to full viewport height
  left: ${props => props.isOpen ? '0' : `-${SIDEBAR_WIDTHS.expanded}`};
  background-color: ${props => props.theme.background.main};
  color: ${props => props.theme.text.primary};
  box-shadow: ${props => props.theme.shadow};
  transition: all ${props => props.theme.transition?.slower || '0.3s'};
  overflow-y: auto;
  z-index: 990;

  // Add padding-top to account for header
  padding-top: ${props => props.headerHeight}px;
  
  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: ${SIDEBAR_WIDTHS.mobile};
    transform: translateX(${props => props.isOpen ? '0' : '-100%'});
  }
`;

const SidebarHeader = styled.div`
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.background.dark};
  min-height: 64px;

  h3 {
    font-size: 1.25rem;
    font-weight: ${props => props.theme.fontWeights.semibold};
    margin: 0;
    transition: opacity ${props => props.theme.transition.default};
    opacity: ${props => props.collapsed && !props.isMobile ? 0.5 : 1};
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text.primary};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all ${props => props.theme.transition.default};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover, &:focus-visible {
    background-color: ${props => rgba(props.theme.text.primary, 0.1)};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const CloseButton = styled(IconButton)`
  display: none;
  position: absolute;
  right: 1rem;
  top: 1rem;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
  }
`;

const SidebarToggle = styled(IconButton)`
  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none;
  }
`;

const ScrollableContent = styled.nav`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: ${props => rgba(props.theme.text.primary, 0.2)} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => rgba(props.theme.text.primary, 0.2)};
    border-radius: 3px;
    
    &:hover {
      background: ${props => rgba(props.theme.text.primary, 0.3)};
    }
  }
`;

const SidebarLink = styled(Link)`
  color: ${props => props.$active ? props.theme.primary.contrast : props.theme.text.primary};
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  background-color: ${props => props.$active ? props.theme.primary.main : 'transparent'};
  transition: all ${props => props.theme.transition.default};
  position: relative;
  font-weight: ${props => props.theme.fontWeights.medium};
  border-left: 4px solid ${props => props.$active ? props.theme.accent : 'transparent'};

  &:hover, &:focus-visible {
    background-color: ${props => props.$active ? 
      props.theme.primary.light : 
      props.theme.background.light};
    outline: none;
  }

  &:active {
    background-color: ${props => props.$active ?
      props.theme.primary.dark :
      props.theme.background.dark};
  }

  svg {
    min-width: 20px;
    width: 20px;
    height: 20px;
    margin-right: ${props => props.collapsed ? '0' : '1rem'};
    transition: margin ${props => props.theme.transition.default};
  }

  span {
    white-space: nowrap;
    opacity: ${props => props.collapsed ? '0' : '1'};
    transition: opacity ${props => props.theme.transition.default};
    font-size: 0.9375rem;
  }
`;

const ContextInfo = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.contextBackground || props.theme.hover};
  margin-bottom: 0.5rem;
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const ContextItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.theme.secondaryText};
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    font-size: 0.7rem;
    color: ${props => props.theme.primary};
  }
`;

const SidebarDivLink = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  color: ${props => props.theme.text};
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  margin: 0.1rem 0;
  position: relative;
  &:hover {
    background: ${props => props.theme.hover};
    color: ${props => props.theme.activeText};
  }
  svg {
    min-width: 20px;
    font-size: 1.1rem;
    color: ${props => props.theme.text};
  }
  span {
    display: ${props => props.$collapsed ? 'none' : 'block'};
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }
  &:focus {
    outline: 2px solid ${props => props.theme.primary};
    outline-offset: -2px;
  }
`;

const SubmenuLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  color: ${props => props.$active ? props.theme.primary.light : props.theme.text.secondary};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid ${props => props.$active ? props.theme.primary.main : 'transparent'};
  margin: 0.1rem 0;
  position: relative;
  &:hover {
    background: ${props => props.theme.hover};
    color: ${props => props.theme.text.primary};
  }
  svg {
    min-width: 20px;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  span {
    display: ${props => props.$collapsed ? 'none' : 'block'};
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    flex: 1;
    min-width: 0;
    font-size: 0.875rem;
    line-height: 1.3;
  }
  &:focus {
    outline: 2px solid ${props => props.theme.primary};
    outline-offset: -2px;
  }
`;

const SubmenuContainer = styled.div`
  padding-left: ${props => props.$collapsed ? '0' : '1rem'};
  display: ${props => props.$open ? 'block' : 'none'};
  background: ${props => props.theme.submenuBackground || 'rgba(0, 0, 0, 0.05)'};
  border-left: 2px solid ${props => props.theme.submenuBorder || props.theme.border};
  margin-left: 1rem;
  margin-right: 0.5rem;
  border-radius: 0 0 4px 4px;
`;

const ProtectedIndicator = styled.span`
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  color: ${props => props.theme.warning || '#ff9800'};
  font-size: 0.8rem;
  display: ${props => props.$collapsed ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 1;
`;

const ContextSwitcher = styled.div`
  padding: 0.5rem 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const SwitcherButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  background: ${props => props.theme.switcherBackground || props.theme.hover};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  color: ${props => props.theme.text};
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;

  &:hover {
    background: ${props => props.theme.hover};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;


const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

// Sidebar Submenu Component
const SidebarSubmenu = ({
  item,
  collapsed,
  isMobile,
  location,
  handleKeyDown,
  parentIndex,
  hasPermission,
  isOperationMfaVerified,
  currentContext
}) => {
  const [open, setOpen] = useState(false);
  const { user, isMfaEnabled } = useAuth(); // Move useAuth to top level

  // Filter children based on permissions and context
  const visibleChildren = item.children?.filter(child => {
    if (!child.visible) return false;

    // Check permissions if specified
    if (child.permission && !hasPermission(child.permission)) {
      console.log(`Child ${child.label} filtered out due to permission ${child.permission}`);
      return false;
    }

    // For MFA requirements, check if MFA is enabled for the user first
    if (child.requiresMfa) {
      // If user doesn't have MFA enabled, don't show MFA-required items
      if (!isMfaEnabled()) {
        console.log(`Child ${child.label} filtered out - MFA not enabled for user`);
        return false;
      }

      // If MFA is enabled, check if operation is verified (but don't require it for display)
      // The individual components will handle MFA verification when accessed
      const isVerified = isOperationMfaVerified(child.mfaOperation || 'FUND_MANAGEMENT');
      console.log(`Child ${child.label} MFA status: ${isVerified ? 'verified' : 'not verified'}`);
      // Don't filter based on MFA verification - let users see the menu items
    }

    // Check branch/department context if specified
    if (child.requiredBranch && currentContext.branch_id !== child.requiredBranch) return false;
    if (child.requiredDepartment && currentContext.department_id !== child.requiredDepartment) return false;

    return true;
  }) || [];

  // Don't render submenu if no visible children
  if (visibleChildren.length === 0) return null;

  return (
    <div>
      <SidebarDivLink
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(prev => !prev);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        tabIndex={0}
        $collapsed={collapsed && !isMobile}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen(prev => !prev);
          }
        }}
      >
        <item.icon aria-hidden="true" />
        <span>{item.label}</span>
        {item.protected && <ProtectedIndicator $collapsed={collapsed}><FaShieldAlt /></ProtectedIndicator>}
        {!collapsed && (open
          ? <FaChevronUp style={{ marginLeft: 'auto', fontSize: '0.75rem', minWidth: '12px' }} />
          : <FaChevronDown style={{ marginLeft: 'auto', fontSize: '0.75rem', minWidth: '12px' }} />
        )}
      </SidebarDivLink>

      <SubmenuContainer $open={open} $collapsed={collapsed}>
        {visibleChildren.map((child, childIndex) => (
          <SubmenuLink
            key={child.id}
            to={child.path}
            id={`sidebar-link-${parentIndex}-${childIndex}`}
            $active={location.pathname === child.path}
            $collapsed={collapsed && !isMobile}
            tabIndex={0}
            onKeyDown={(e) => handleKeyDown(e, parentIndex)}
            aria-current={location.pathname === child.path ? 'page' : undefined}
          >
            <child.icon aria-hidden="true" />
            <span>{child.label}</span>
            {child.requiresMfa && <ProtectedIndicator $collapsed={collapsed}><FaShieldAlt /></ProtectedIndicator>}
          </SubmenuLink>
        ))}
      </SubmenuContainer>
    </div>
  );
};

// Main Sidebar Component
const Sidebar = ({
  onToggle,
  collapsed = false,
  isOpen = false,
  theme = defaultTheme,
  onReorder
}) => {
  const loadAttemptRef = useRef(0);
  const maxLoadAttempts = 3;
  const lastLoadTimeRef = useRef(0);
  const minLoadInterval = 5000;

  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const {
    user,
    hasPermission,
    isOperationMfaVerified,
    getCurrentContext,
    currentBranch,
    currentDepartment,
    isCrossBranch,
    isCrossDepartment,
    availableBranches,
    availableDepartments,
    switchBranch,
    switchDepartment,
    isMfaEnabled,
    authChecked,
    isInitialized,
    loadBranchDepartmentContext
  } = useAuth();

  const currentContext = getCurrentContext();

  const getBranchDisplayName = () => {
    console.log('Getting branch display name...');
    console.log('currentBranch:', currentBranch);
    console.log('user.branch:', user?.branch);
    console.log('user.branch_name:', user?.branch_name);

    // Priority order for branch name
    if (currentBranch?.name) {
      console.log('Using currentBranch.name:', currentBranch.name);
      return currentBranch.name;
    }

    if (user?.branch?.name) {
      console.log('Using user.branch.name:', user.branch.name);
      return user.branch.name;
    }

    if (user?.branch_name) {
      console.log('Using user.branch_name:', user.branch_name);
      return user.branch_name;
    }

    // Try to find in available branches using user's branch_id
    if (user?.branch_id && availableBranches?.length > 0) {
      const foundBranch = availableBranches.find(b => b.id === user.branch_id);
      if (foundBranch?.name) {
        console.log('Found branch in availableBranches:', foundBranch.name);
        return foundBranch.name;
      }
    }

    // If user is authenticated but no branch data, show fallback
    if (authChecked && isInitialized && user) {
      console.log('No branch name found but user authenticated, showing fallback');
      return 'Default Branch';
    }

    console.log('No branch name found, returning Loading...');
    return 'Loading...';
  };

  const getDepartmentDisplayName = useCallback(() => {
    console.log('Getting department display name...');
    console.log('currentDepartment:', currentDepartment);
    console.log('user.department:', user?.department);
    console.log('user.department_name:', user?.department_name);

    if (currentDepartment?.name) {
      console.log('Using currentDepartment.name:', currentDepartment.name);
      return currentDepartment.name;
    }

    if (user?.department?.name) {
      console.log('Using user.department.name:', user.department.name);
      return user.department.name;
    }

    if (user?.department_name) {
      console.log('Using user.department_name:', user.department_name);
      return user.department_name;
    }

    if (user?.department_id && availableDepartments?.length > 0) {
      const foundDepartment = availableDepartments.find(d => d.id === user.department_id);
      if (foundDepartment?.name) {
        console.log('Found department in availableDepartments:', foundDepartment.name);
        return foundDepartment.name;
      }
    }

    // If user is authenticated but no department data, check if department is required
    if (authChecked && isInitialized && user) {
      console.log('No department name found but user authenticated');
      // If departments are optional in your system or user doesn't have one assigned
      if (!user.department_id) {
        return 'No Department';
      }
      return 'General Department';
    }

    console.log('No department name found, returning Loading...');
    return 'Loading...';
  }, [currentDepartment, user, availableDepartments, authChecked, isInitialized]); // Add dependencies

  // Debug logging
  useEffect(() => {
    const shouldLoadContext = () => {
      // Basic checks
      if (!authChecked || !isInitialized || !user) {
        return false;
      }

      // Check if we've exceeded max attempts
      if (loadAttemptRef.current >= maxLoadAttempts) {
        console.log('Max load attempts reached, stopping');
        return false;
      }

      // Check minimum time interval
      const now = Date.now();
      if (now - lastLoadTimeRef.current < minLoadInterval) {
        console.log('Too soon since last load attempt');
        return false;
      }

      // Check if we actually need to load
      const branchName = getBranchDisplayName();
      const deptName = getDepartmentDisplayName();

      const needsLoad = (branchName === 'Loading...' || deptName === 'Loading...') &&
                       typeof loadBranchDepartmentContext === 'function';

      return needsLoad;
    };

    if (shouldLoadContext()) {
      console.log(`Loading context attempt ${loadAttemptRef.current + 1}/${maxLoadAttempts}`);
      loadAttemptRef.current++;
      lastLoadTimeRef.current = Date.now();

      const timer = setTimeout(() => {
        loadBranchDepartmentContext().catch(console.error);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [
    authChecked,
    isInitialized,
    user?.id,
    currentBranch?.id,
    currentDepartment?.id,
    loadBranchDepartmentContext,
    getDepartmentDisplayName // Add this dependency
  ]);

  // Reset load attempts when user changes
  useEffect(() => {
    loadAttemptRef.current = 0;
    lastLoadTimeRef.current = 0;
  }, [user?.id]);

  const [menuItems, setMenuItems] = useState([
    {
      id: '1',
      label: 'Dashboard',
      path: '/',
      icon: FaHome,
      visible: true
    },
    {
      id: 'tax-categories',
      label: 'Tax Categories',
      path: '/tax-categories',
      icon: FaBalanceScale,
      visible: true,
      permission: 'products.view_product'
    },
    {
      id: 'fund-management',
      label: 'Fund Management',
      icon: FaMoneyCheckAlt,
      visible: true,
      protected: true,
      permission: 'fund_management.view_fundsource',
      children: [
        {
          id: 'fund-sources',
          label: 'Fund Sources',
          path: '/fund-management/sources',
          icon: FaSitemap,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundsource'
        },
        {
          id: 'fund-account',
          label: 'Fund Account',
          path: '/fund-management/accounts',
          icon: FaWallet,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundaccount'
        },
        {
          id: 'fund-transactions',
          label: 'Fund Transactions',
          path: '/fund-management/transactions',
          icon: FaExchangeAlt,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundtransaction'
        },
        {
          id: 'fund-retirement',
          label: 'Fund Retirement',
          path: '/fund-management/retirements',
          icon: FaCalendarCheck,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundretirement'
        },
        {
          id: 'fund-approvals',
          label: 'Fund Approvals',
          path: '/fund-management/approvals',
          icon: FaCheckDouble,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT',
          permission: 'fund_management.view_fundapproval'
        },
        {
          id: 'fund-reporting',
          label: 'Fund Reporting',
          path: '/fund-management/reporting',
          icon: FaChartLine,
          visible: true,
          requiresMfa: true,
          mfaOperation: 'FUND_MANAGEMENT'
        }
      ]
    },
    {
      id: 'bakery',
      label: 'Bakery Management',
      icon: FaBreadSlice,
      visible: true,
      permission: 'bakery_recipe.access',
      children: [
        {
          id: 'bakery-performance',
          label: 'Performance Dashboard',
          path: '/bakery/performance',
          icon: FaChartLine,
          visible: true,
          permission: 'bakery_recipe.view_performance'
        },
        {
          id: 'bakery-requisition',
          label: 'Requisition Management',
          path: '/bakery/requisition',
          icon: FaFileAlt,
          visible: true,
          permission: 'bakery_recipe.view_requisition'
        },
        {
          id: 'bakery-tracking',
          label: 'Order Tracking',
          path: '/bakery/tracking',
          icon: FaTruck,
          visible: true,
          permission: 'bakery_recipe.view_tracking'
        },
        {
          id: 'bakery-trading',
          label: 'Trading & Suppliers',
          path: '/bakery/trading',
          icon: FaDollarSign,
          visible: true,
          permission: 'bakery_recipe.view_trading'
        },
        {
          id: 'bakery-usage',
          label: 'Material Usage',
          path: '/bakery/usage',
          icon: FaBoxes,
          visible: true,
          permission: 'bakery_recipe.view_usage'
        },
	{
          id: 'bakery-raw-materials',
          label: 'Raw Materials',
          path: '/bakery/raw-materials',
          icon: FaWarehouse,
          visible: true,
          permission: 'bakery_recipe.view_rawmaterials'
        },
	{
          id: 'bakery-raw-material-stock-correction',
          label: 'Stock Correction',
          path: '/bakery/raw-materials/stock-adjustment',
          icon: FaWarehouse,
          visible: true,
          superuserOnly: true
        },
	{
          id: 'bakery-recipe-management',
          label: 'Recipe Management',
          path: '/bakery/recipe-management',
          icon: FaUtensils,
          visible: true,
          permission: 'bakery_recipe.view_recipe'
        }
      ]
    },
    {
      id: 'photography',
      label: 'Photography',
      icon: FaCamera,
      visible: true,
      permission: 'photography.module_access',
      children: [
        {
          id: 'photography-projects',
          label: 'All Projects',
          path: '/photography/projects',
          icon: FaPhotoVideo,
          visible: true,
          permission: 'photography.view_photoproject'
        },
        {
          id: 'photography-equipment',
          label: 'Equipment',
          path: '/photography/equipment',
          icon: FaCameraRetro,
          visible: true,
          permission: 'photography.view_photographyequipment'
        },
	{
          id: 'photography-my-assignments',
          label: 'My Assignments',
          path: '/photography/my-assignments',
          icon: FaClipboardCheck,
          visible: true,
          permission: 'photography.view_equipmentassignment'
        },
	{
          id: 'photography-incidents',
          label: 'Equipment Incidents',
          path: '/photography/incidents',
          icon: FaExclamationTriangle,
          visible: true,
          permission: 'photography.view_equipmentincident',
          badge: 'warning'
        }
      ]
    },
    {
      id: 'logistics',
      label: 'Logistics',
      icon: FaTruck,
      visible: true,
      permission: 'logistics.manage_delivery',
      children: [
        {
          id: 'logistics-deliveries',
          label: 'Deliveries',
          path: '/logistics/deliveries',
          icon: FaTruck,
          visible: true,
          permission: 'logistics.manage_delivery'
        },
        {
          id: 'logistics-drivers',
          label: 'Drivers',
          path: '/logistics/drivers',
          icon: FaIdCard,
          visible: true,
          permission: 'logistics.manage_driver'
        },
        {
          id: 'logistics-vehicles',
          label: 'Vehicles',
          path: '/logistics/vehicles',
          icon: FaCar,
          visible: true,
          permission: 'logistics.manage_vehicle'
        },
        {
          id: 'logistics-routes',
          label: 'Routes',
          path: '/logistics/routes',
          icon: FaRoute,
          visible: true,
          permission: 'logistics.manage_delivery_route'
        },
        {
          id: 'logistics-maintenance',
          label: 'Maintenance Logs',
          path: '/logistics/maintenance',
          icon: FaWrench,
          visible: true,
          permission: 'logistics.manage_vehicle_maintenance'
        }
      ]
    },
    {
      id: 'salon',
      label: 'Salon',
      icon: FaCut,
      visible: true,
      permission: 'salon.view_salonequipment',
      children: [
        {
          id: 'salon-services',
          label: 'Services',
          path: '/salon/services',
          icon: FaClipboardCheck,
          visible: true,
          permission: 'salon.view_salonservice'
        },
        {
          id: 'salon-equipment',
          label: 'Equipment',
          path: '/salon/equipment',
          icon: FaWarehouse,
          visible: true,
          permission: 'salon.view_salonequipment'
        }
      ]
    },
    {
      id: '3',
      label: 'Invoices',
      path: '/invoices',
      icon: FaFileInvoiceDollar,
      visible: true,
      permission: 'invoices.view_invoice'
    },
    {
      id: '4',
      label: 'Receipts',
      path: '/receipts',
      icon: FaReceipt,
      visible: true
    },
    {
      id: '5',
      label: 'Products',
      path: '/products',
      icon: FaBox,
      visible: true,
      permission: 'products.view_product'
    },
    {
      id: '6',
      label: 'Stock Levels',
      path: '/stock-levels',
      icon: FaLayerGroup,
      visible: true,
      permission: 'stock_adjustments.view_adjustment'
    },
    {
      id: '7',
      label: 'Orders',
      path: '/orders',
      icon: FaShoppingCart,
      visible: true,
      permission: 'core.view_order'
    },
    {
      id: '8',
      label: 'Customers',
      path: '/customers',
      icon: FaUsers,
      visible: true
    },
    {
      id: '9',
      label: 'User Roles',
      path: '/user-roles',
      icon: FaUserShield,
      visible: true,
      permission: 'user.view_all'
    },
    {
      id: '10',
      label: 'User Management',
      path: '/user-management',
      icon: FaUsers,
      visible: true,
      permission: 'user.view_all'
    },
    {
      id: '11',
      label: 'Reports',
      path: '/reports',
      icon: FaChartBar,
      visible: true,
      permission: 'reports.view_report'
    },
    {
      id: '12',
      label: 'Transactions',
      path: '/transactions',
      icon: FaExchangeAlt,
      visible: true,
      permission: 'transactions.view_transaction'
    },
    {
      id: '14',
      label: 'Audit Compliance',
      path: '/audit-compliance',
      icon: FaShieldAlt,
      visible: true,
      requiresMfa: true,
      mfaOperation: 'AUDIT_COMPLIANCE',
      permission: 'audit_compliance.view_dashboard'
    },
    {
      id: '13',
      label: 'Account Settings',
      path: '/account',
      icon: FaCog,
      visible: true
    }
  ]);

  const handleWindowResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [handleWindowResize]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const flatItems = menuItems.filter(item => !item.children);
    const oldIndex = flatItems.findIndex(item => item.id === active.id);
    const newIndex = flatItems.findIndex(item => item.id === over.id);

    const reordered = arrayMove(flatItems, oldIndex, newIndex);
    onReorder?.(reordered);
    setMenuItems(prev =>
      prev.map(item =>
        item.children ? item : reordered.find(r => r.id === item.id) || item
      )
    );
  }, [menuItems, onReorder]);

  const handleKeyDown = useCallback((e, index) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const targetIndex = e.key === 'ArrowDown'
        ? Math.min(index + 1, menuItems.length - 1)
        : Math.max(index - 1, 0);
      document.getElementById(`sidebar-link-${targetIndex}`)?.focus();
    }
  }, [menuItems.length]);

  const handleBranchSwitch = async (branchId) => {
    try {
      await switchBranch(branchId);
    } catch (error) {
      console.error('Failed to switch branch:', error);
    }
  };

  const handleDepartmentSwitch = async (departmentId) => {
    try {
      await switchDepartment(departmentId);
    } catch (error) {
      console.error('Failed to switch department:', error);
    }
  };

  const visibleMenuItems = menuItems.filter(item => {
    if (!item.visible) return false;

    if (item.superuserOnly && !(user?.is_superuser === true)) {
      console.log(`Menu item ${item.label} filtered out: superuser-only`);
      return false;
    }

    if (item.permission && !hasPermission(item.permission)) {
      console.log(`Menu item ${item.label} filtered out due to permission ${item.permission}`);
      return false;
    }

    if (item.requiredBranch && currentContext.branch_id !== item.requiredBranch) return false;
    if (item.requiredDepartment && currentContext.department_id !== item.requiredDepartment) return false;

    return true;
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Don't render sidebar until auth is checked and initialized
  if (!authChecked || !isInitialized) {
    return null; // or a loading spinner
  }

  return (
    <ThemeProvider theme={theme}>
      <SidebarContainer
        collapsed={collapsed}
        isOpen={isOpen}
        role="navigation"
        aria-label="Main navigation"
      >
        <SidebarHeader collapsed={collapsed} isMobile={isMobile}>
          <h3>{collapsed && !isMobile ? 'M' : 'Menu'}</h3>
          <SidebarToggle
            onClick={onToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </SidebarToggle>
          <CloseButton
            onClick={onToggle}
            isOpen={isOpen}
            aria-label="Close sidebar"
          >
            <FaTimes />
          </CloseButton>
        </SidebarHeader>

        {/* Branch/Department Context Display */}
        <ContextInfo collapsed={collapsed}>
          <ContextItem>
            <FaBuilding />
            <span>{getBranchDisplayName()}</span>
          </ContextItem>
          <ContextItem>
            <FaDepartment />
            <span>{getDepartmentDisplayName()}</span>
          </ContextItem>
        </ContextInfo>

        {/* Branch/Department Switcher */}
        {(
          (availableBranches.length > 1 && (isCrossBranch || user?.is_superuser || user?.is_staff)) ||
          (availableDepartments.length > 1 && (isCrossDepartment || user?.is_superuser || user?.is_staff))
        ) && (
          <ContextSwitcher collapsed={collapsed}>
            {availableBranches.length > 1 && (isCrossBranch || user?.is_superuser || user?.is_staff) && (
              <div>
                <label style={{ fontSize: '0.8rem', marginBottom: '0.25rem', display: 'block' }}>
                  Switch Branch:
                </label>
                <select
                  value={currentBranch?.id || ''}
                  onChange={(e) => handleBranchSwitch(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '0.25rem',
                    fontSize: '0.8rem',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    backgroundColor: 'white',
                    marginBottom: '0.5rem'
                  }}
                >
                  {availableBranches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} {branch.id === currentBranch?.id && '(Current)'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {availableDepartments.length > 1 && (isCrossDepartment || user?.is_superuser || user?.is_staff) && (
              <div>
                <label style={{ fontSize: '0.8rem', marginBottom: '0.25rem', display: 'block' }}>
                  Switch Department:
                </label>
                <select
                  value={currentDepartment?.id || ''}
                  onChange={(e) => handleDepartmentSwitch(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '0.25rem',
                    fontSize: '0.8rem',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    backgroundColor: 'white'
                  }}
                >
                  {availableDepartments.map(department => (
                    <option key={department.id} value={department.id}>
                      {department.name} {department.id === currentDepartment?.id && '(Current)'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </ContextSwitcher>
        )}

        <ScrollableContent>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visibleMenuItems.filter(item => !item.children).map(item => item.id)}
              strategy={verticalListSortingStrategy}
            >
              {visibleMenuItems.map((item, index) => (
                <SortableItem key={item.id} id={item.id}>
                  {item.children ? (
                    <SidebarSubmenu
                      item={item}
                      collapsed={collapsed}
                      isMobile={isMobile}
                      location={location}
                      handleKeyDown={handleKeyDown}
                      parentIndex={index}
                      hasPermission={hasPermission}
                      isOperationMfaVerified={isOperationMfaVerified}
                      currentContext={currentContext}
                    />
                  ) : (
                    <SidebarLink
                      to={item.path}
                      id={`sidebar-link-${index}`}
                      $active={location.pathname === item.path}
                      $collapsed={collapsed && !isMobile}
                      tabIndex={0}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      aria-current={location.pathname === item.path ? 'page' : undefined}
                    >
                      <item.icon aria-hidden="true" />
                      <span>{item.label}</span>
                      {item.protected && <ProtectedIndicator $collapsed={collapsed}><FaShieldAlt /></ProtectedIndicator>}
                    </SidebarLink>
                  )}
                </SortableItem>
              ))}
            </SortableContext>
          </DndContext>
        </ScrollableContent>
      </SidebarContainer>
    </ThemeProvider>
  );
};

export default memo(Sidebar);
